import {startTransition, useCallback, useMemo} from 'react'

import {mergeabilityStatus, type Status} from '../helpers/mergeability-status'
import {useMergeBoxPageData} from '../page-data/loaders/use-merge-box-page-data'
import {useStatusChecksPageDataWithoutError} from '../page-data/loaders/use-status-checks-page-data'
import {useMergeabilityLiveUpdates, type Channels} from './use-mergeability-live-updates'
import {useMergeMethodContext} from '../contexts/MergeMethodContext'

interface UseMergeabilityStatusOptions {
  headSha: string
  channels: Channels | null
}

export interface UseMergeabilityStatusResult {
  status: Status | null
  isLoading: boolean
}

export const EMPTY_CHANNELS: Channels = {
  stateChannel: null,
  deployedChannel: null,
  reviewStateChannel: null,
  workflowsChannel: null,
  mergeQueueChannel: null,
  headRefChannel: null,
  baseRefChannel: null,
  gitMergeStateChannel: null,
  pullRequestChannel: null,
} as const

export function useMergeabilityStatus({headSha, channels}: UseMergeabilityStatusOptions): UseMergeabilityStatusResult {
  const {mergeMethod} = useMergeMethodContext()
  const {data: mergeBoxData, refetch, isLoading: mergeBoxLoading} = useMergeBoxPageData({mergeMethod})

  const {data: statusChecksData, isLoading: checksLoading} = useStatusChecksPageDataWithoutError({
    pullRequestHeadSha: headSha,
  })

  const refetchQuery = useCallback(() => {
    startTransition(() => {
      refetch({cancelRefetch: false})
    })
  }, [refetch])

  useMergeabilityLiveUpdates({refetchQuery, channels: channels ?? EMPTY_CHANNELS})

  const isLoading = mergeBoxLoading || checksLoading

  const status = useMemo(() => {
    if (!mergeBoxData) return null
    return mergeabilityStatus({...mergeBoxData, statusChecksData})
  }, [mergeBoxData, statusChecksData])

  return {status, isLoading}
}
